import type { OpenAPI } from "simplydo/interfaces";

import api from "api";

import type {
  AnnouncementConditionConfig,
  AnnouncementSubConditionOption,
} from "./types";

type ChallengeTemplate = OpenAPI.GET<"/challenges/{id}/template">["response"];
type EntityType = "organisation" | "group" | "challenge";

type PostableGroup = {
  name: string;
  userCount: number;
  isCurrentlyInChallengeAudience?: boolean;
};

const getTagOptions = (
  entityType: EntityType,
  entityId: string,
  callback: (tags: { _id: string; name: string }[]) => void,
) => {
  api.tags.getAllForEntity(
    entityType,
    entityId,
    true,
    (tags) => {
      callback(tags.map((tag) => ({ _id: tag._id, name: tag.value })));
    },
    () => {},
  );
};

const getRoleOptions = (
  entityType: EntityType,
  entityId: string,
  callback: (roles: { _id: string; name: string }[]) => void,
) => {
  api.roles.get(
    entityType,
    entityId,
    { for_type: entityType, for_id: entityId },
    ({ roles }) => {
      callback(roles.map((role) => ({ _id: role._id, name: role.name })));
    },
    () => {},
  );
};

const getLabelOptions = (
  challengeId: string,
  callback: (options: AnnouncementSubConditionOption[]) => void,
) => {
  api.challenges.getLabels(
    ({ labels }) => {
      callback(labels?.map((label) => ({ _id: label, name: label })) || []);
    },
    () => {},
  );
};

export const organisationStandardConditions: AnnouncementConditionConfig[] = [
  {
    value: "hasTag",
    text: "admin.announcements.recipients.conditions.userTag",
    subConditions: [
      {
        key: "tags",
        type: "dropdown",
        placeholder: "Select a tag",
        getOptions: (
          orgId: string,
          callback: (options: AnnouncementSubConditionOption[]) => void,
        ) => {
          getTagOptions("organisation", orgId, callback);
        },
      },
    ],
  },
  {
    value: "hasCreatedIdea",
    text: "admin.announcements.recipients.conditions.hasCreatedIdea",
  },
];

export const organisationAutomaticConditions: AnnouncementConditionConfig[] = [
  { value: "register", text: "admin.announcements.form.options.register" },
  {
    value: "createChallenge",
    text: "admin.announcements.form.options.createChallenge",
  },
  { value: "createIdea", text: "admin.announcements.form.options.createIdea" },
  { value: "referredTo", text: "admin.announcements.form.options.referredTo" },
  {
    value: "referredFrom",
    text: "admin.announcements.form.options.referredFrom",
  },
];

export const challengeStandardConditions: AnnouncementConditionConfig[] = [
  {
    value: "following",
    text: "challenge.settings.announcements.recipients.conditions.following",
  },
  {
    value: "started",
    text: "challenge.settings.announcements.recipients.conditions.started",
  },
  {
    value: "submitted",
    text: "challenge.settings.announcements.recipients.conditions.submitted",
  },
  {
    value: "notSubmitted",
    text: "challenge.settings.announcements.recipients.conditions.notSubmitted",
  },
  {
    value: "pinned",
    text: "challenge.settings.announcements.recipients.conditions.pinned",
  },
  {
    value: "notPinned",
    text: "challenge.settings.announcements.recipients.conditions.notPinned",
  },
  {
    value: "label",
    text: "challenge.settings.announcements.recipients.conditions.hasLabel",
    subConditions: [
      {
        key: "labels",
        type: "dropdown",
        placeholder: "Select a label",
        multiple: true,
        getOptions: (
          challengeId: string,
          callback: (options: AnnouncementSubConditionOption[]) => void,
        ) => {
          getLabelOptions(challengeId, callback);
        },
      },
    ],
  },
  {
    value: "notLabel",
    text: "challenge.settings.announcements.recipients.conditions.notHasLabel",
    subConditions: [
      {
        key: "labels",
        type: "dropdown",
        multiple: true,
        getOptions: (
          challengeId: string,
          callback: (options: AnnouncementSubConditionOption[]) => void,
        ) => {
          getLabelOptions(challengeId, callback);
        },
      },
    ],
  },
  {
    value: "audience",
    text: "challenge.settings.announcements.recipients.conditions.audience",
  },
  {
    value: "lane",
    text: "challenge.settings.announcements.recipients.conditions.lane",
    subConditions: [
      {
        key: "lanes",
        type: "dropdown",
        placeholder: "Select the lane you would like to target",
        getOptions: (
          challengeId: string,
          callback: (options: AnnouncementSubConditionOption[]) => void,
        ) => {
          api.boards.getProjectLanes(
            challengeId,
            ({ lanes, defaultLane }) => {
              if (defaultLane) {
                callback([defaultLane, ...lanes]);
              } else {
                callback(lanes);
              }
            },
            () => {},
          );
        },
      },
    ],
  },
  {
    value: "userHasTag",
    text: "challenge.settings.announcements.recipients.conditions.userTag",
    subConditions: [
      {
        key: "tags",
        type: "dropdown",
        getOptions: (
          challengeId: string,
          callback: (options: AnnouncementSubConditionOption[]) => void,
        ) => {
          getTagOptions("challenge", challengeId, callback);
        },
      },
    ],
  },
  {
    value: "userInGroup",
    text: "challenge.settings.announcements.recipients.conditions.userGroup",
    subConditions: [
      {
        key: "group",
        type: "dropdown",
        getOptions: (
          challengeId: string,
          callback: (options: AnnouncementSubConditionOption[]) => void,
        ) => {
          api.challenges.getPostable(
            challengeId,
            ({ groups }) => {
              callback(
                (groups as PostableGroup[]).filter(
                  (group) => group.isCurrentlyInChallengeAudience,
                ),
              );
            },
            () => {},
          );
        },
      },
    ],
  },
  {
    value: "projectAssigned",
    text: "challenge.settings.announcements.recipients.conditions.projectAssigned",
  },
  {
    value: "projectAssignedNonCompleted",
    text: "challenge.settings.announcements.recipients.conditions.projectAssignedNonCompleted",
  },
  {
    value: "assessor",
    text: "challenge.settings.announcements.recipients.conditions.assessor",
  },
  {
    value: "unsubmittedAssessment",
    text: "challenge.settings.announcements.recipients.conditions.unsubmittedAssessment",
  },
];

export const challengeAutomaticConditions: AnnouncementConditionConfig[] = [
  {
    value: "createIdea",
    text: "challenge.settings.announcements.recipients.automatic.starts",
  },
  {
    value: "submitIdea",
    text: "challenge.settings.announcements.recipients.automatic.submits",
  },
  {
    value: "submittedAllAssessmentsForChallenge",
    text: "challenge.settings.announcements.recipients.automatic.assessmentsSubmitted",
    subConditions: [
      {
        key: "assessmentRounds",
        placeholder: "Select an assessment",
        getOptions: (
          challengeId: string,
          callback: (options: AnnouncementSubConditionOption[]) => void,
        ) => {
          api.challenges.getTemplate(
            challengeId,
            (template: ChallengeTemplate) => {
              callback(
                template.assessments?.rounds?.map((round) => ({
                  value: round.id,
                  text: round.name,
                })) || [{ value: "legacy", text: "Assessment" }],
              );
            },
            () => {},
          );
        },
      },
    ],
  },
  {
    value: "submitIdeaWithConditions",
    text: "challenge.settings.announcements.recipients.automatic.ideaFieldCondition",
    subConditions: [
      {
        key: "ideaFields",
        placeholder: "Select a field",
        getOptions: (
          challengeId: string,
          callback: (options: AnnouncementSubConditionOption[]) => void,
        ) => {
          api.challenges.getTemplate(
            challengeId,
            (template: ChallengeTemplate) => {
              const fields = (template.body ?? []).flatMap((section) =>
                section.fields.map((field) => ({
                  ...field,
                  section: `in ${section.name}`,
                })),
              );
              callback(fields);
            },
            () => {},
          );
        },
      },
    ],
  },
  {
    value: "moveToLane",
    text: "challenge.settings.announcements.recipients.automatic.moveToLane",
    subConditions: [
      {
        key: "moveToLane",
        placeholder: "Select a lane",
        getOptions: (
          challengeId: string,
          callback: (options: AnnouncementSubConditionOption[]) => void,
        ) => {
          api.boards.getProjectLanes(
            challengeId,
            ({ lanes, defaultLane }) => {
              const formattedLanes = lanes.map((lane) => ({
                key: lane._id,
                text: lane.name,
                value: lane._id,
              }));
              if (defaultLane) {
                callback([...formattedLanes]);
              } else {
                callback(formattedLanes);
              }
            },
            () => {},
          );
        },
      },
    ],
  },
];

export const groupStandardConditions: AnnouncementConditionConfig[] = [
  {
    value: "hasTag",
    text: "groups.announcements.recipients.conditions.userTag",
    subConditions: [
      {
        key: "tags",
        type: "dropdown",
        placeholder: "Select a tag",
        getOptions: (
          groupId: string,
          callback: (options: AnnouncementSubConditionOption[]) => void,
        ) => {
          getTagOptions("group", groupId, callback);
        },
      },
    ],
  },
  {
    value: "hasRole",
    text: "groups.announcements.recipients.conditions.userRole",
    subConditions: [
      {
        key: "roles",
        type: "dropdown",
        placeholder: "Select a role",
        getOptions: (
          groupId: string,
          callback: (options: AnnouncementSubConditionOption[]) => void,
        ) => {
          getRoleOptions("group", groupId, callback);
        },
      },
    ],
  },
  {
    value: "noLogIn",
    text: "groups.announcements.recipients.conditions.noLogIn",
  },
  {
    value: "notActiveSince",
    text: "groups.announcements.recipients.conditions.notActiveSince",
    subConditions: [{ key: "since", type: "date" }],
  },
];

export const groupAutomaticConditions: AnnouncementConditionConfig[] = [
  { value: "joinGroup", text: "groups.announcements.joinsGroup" },
];
